import * as Yup from 'yup'
import isValid from 'date-fns/isValid'
import parse from 'date-fns/parse'
import addYears from 'date-fns/addYears'
import isBefore from 'date-fns/isBefore'
import { createPhoneSchema } from 'utils/phone'
import { NAME_REGEX } from 'constants/general'
// Validates a zip code format: Must contain at least one number, allows letters and numbers,
// optional hyphen followed by more alphanumeric characters (e.g. "12345", "A1B2C3", "12345-6789")
const ZIP_CODE_REGEX = /^[A-Za-z0-9]+([ -][A-Za-z0-9]+)?$/

// Validates that the submitted country is one of the allowed (selectable) countries.
// Passes when empty (let .required handle that) or when the allowed list is not yet
// loaded, so it only ever rejects a value that is explicitly disallowed.
const countryAllowedTest = (allowedCountryCodes, languageData) => ({
  name: 'country-allowed',
  message: languageData?.countryNotAllowed || 'Selected country is not available',
  test: (value) =>
    !value || !allowedCountryCodes?.length || allowedCountryCodes.includes(value)
})

// Schema for EN with Extra Validations
export const userSigninSchemaEN = (regFields, languageData, allowedCountryCodes = []) =>
  regFields &&
  Yup.object().shape({
    email: regFields.email &&
      Yup.string().email(languageData?.invalidEmail).max(50, languageData?.max50Characters).required(languageData?.emailRequired),

    password: regFields.password &&
      Yup.string()
        .min(8, languageData?.min8Chars)
        .max(50, languageData?.max50Characters)
        .matches(/[a-z]/, languageData?.mustHaveLowercase) // at least one lowercase
        .matches(/[A-Z]/, languageData?.mustHaveUppercase) // at least one uppercase
        .matches(/\d/, languageData?.mustHaveNumber) // at least one number
        .matches(/[^a-zA-Z0-9]/, languageData?.mustHaveSpecialChar) // at least one special character
        .matches(/^\S*$/, languageData?.noSpacesAllowed) // no space allowed
        .required(languageData?.passwordRequired)
        .test('password-not-match-email-or-username', languageData?.passwordCannotMatchEmailOrUsername || 'Password cannot match email or username',
          function (value) {
            const { email, username } = this.parent
            if (!value) return true // Skip test if password is empty (other validators will catch it)

            const lowerPassword = value.toLowerCase()
            const lowerEmail = email?.toLowerCase() || ''
            const lowerUsername = username?.toLowerCase() || ''

            return (
              lowerPassword !== lowerEmail &&
              lowerPassword !== lowerUsername
            )
          }),
    confirmPassword: regFields.confirmPassword &&
      Yup.string()
        .max(50, languageData?.max50Characters)
        .oneOf([Yup.ref('password'), null], languageData?.matchPassword)
        .required(languageData?.confirmPasswordRequired),

    firstName: regFields.firstName &&
      Yup.string()
        .min(3, languageData?.fName3Chars)
        .matches(NAME_REGEX, languageData?.signup_invalidNameChars)
        .max(50, languageData?.max50Characters)
        .required(languageData?.firstNameRequired),

    lastName: regFields.lastName === 2
      ? Yup.string()
        .matches(NAME_REGEX, languageData?.signup_invalidNameChars)
        .min(3, languageData?.lName3Chars).max(50, languageData?.max50Characters).required(languageData?.lastNameRequired)
      : Yup.string()
        .matches(NAME_REGEX, languageData?.signup_invalidNameChars)
        .min(3, languageData?.lName3Chars).max(50, languageData?.max50Characters).nullable(),

    phone: regFields.phone === 2
      ? createPhoneSchema(languageData?.invalidPhone).required(languageData?.phoneRequired).nullable()
      : regFields.phone !== 0 && createPhoneSchema(languageData?.invalidPhone).nullable(),

    dateOfBirth: regFields.dateOfBirth === 2
      ? Yup.string()
        .nullable() // Allow null values for the date
        .test('complete-date', languageData?.dateOfBirthRequired, function (value) {
          if (!value || value === 'YYYY/MM/DD') return false
          const [year, month, day] = value.split('/')
          return year !== 'YYYY' && month !== 'MM' && day !== 'DD'
        })
        .test('valid-date', languageData?.validDOB, function (value) {
          if (!value) return false
          const [year, month, day] = value.split('/')
          const parsed = parse(`${year}-${month}-${day}`, 'yyyy-MM-dd', new Date())
          return isValid(parsed) // Ensure the date is valid
        })
        .test('age', languageData?.mustBe18, function (value) {
          if (!value) return false
          const [year, month, day] = value.split('/')
          const dob = parse(`${year}-${month}-${day}`, 'yyyy-MM-dd', new Date())
          if (!isValid(dob)) return false
          const today = new Date()
          return !isBefore(today, addYears(dob, 18)) // Ensure the user is 18+
        })
      : regFields.dateOfBirth !== 0 && Yup.string().nullable(), // Allow nullable value for empty field

    gender: regFields.gender === 2
      ? Yup.string().required(languageData?.genderRequired)
      : regFields.gender !== 0 && Yup.string().nullable(),

    username: regFields.username === 2
      ? Yup.string().max(20, languageData?.max20Characters)
        .min(3, languageData?.min3Characters)
        .matches(/^[A-Za-z][A-Za-z0-9._]*$/, languageData?.userNameAllowed)
        .required(languageData?.userNameRequired)
      : regFields.username !== 0 && Yup.string().max(20, languageData?.max20Characters)
        .min(3, languageData?.min3Characters)
        .matches(/^[A-Za-z][A-Za-z0-9._]*$/, languageData?.userNameAllowed)
        .nullable(),

    address: regFields.address === 2
      ? Yup.string().max(100, languageData?.max100Characters)
        .required(languageData?.addressRequired)
        .nullable()
      : regFields.address !== 0 && Yup.string().max(100, languageData?.max100Characters)
        .nullable(),

    city: regFields.address === 2
      ? Yup.string().max(50, languageData?.max50Characters)
        .required(languageData?.cityRequired).nullable()
      : regFields.address !== 0 && Yup.string()
        .max(50, languageData?.max50Characters).nullable(),

    zipCode: regFields.address === 2
      ? Yup.string()
        .required(languageData?.postCodeRequired)
        .matches(ZIP_CODE_REGEX, languageData?.zipCodeValidationMessage)
        .nullable()
      : regFields.address !== 0 && Yup.string()
        .nullable(),

    currencyCode: regFields.currencyCode &&
      Yup.string().required(languageData?.currencyCodeRequired),

    countryCode: regFields.countryCode === 2
      ? Yup.string()
        .required(languageData?.countryRequired)
        .test(countryAllowedTest(allowedCountryCodes, languageData))
      : regFields.countryCode !== 0 &&
        Yup.string()
          .nullable()
          .test(countryAllowedTest(allowedCountryCodes, languageData)),

    preferredLanguage: regFields.preferredLanguage === 2
      ? Yup.string().required(languageData?.preferredLanguageRequired)
      : regFields.preferredLanguage !== 0 && Yup.string().nullable(),

    affiliateCode: regFields.affiliateCode === 2
      ? Yup.string().max(50, languageData?.max50Characters).required(languageData?.affiliateCodeRequired)
      : regFields.affiliateCode !== 0 && Yup.string().max(50, languageData?.max50Characters).nullable()
  })

// Schema for Other Languages
export const userSigninSchema = (regFields, languageData, allowedCountryCodes = []) =>
  regFields &&
  Yup.object().shape({
    email: regFields.email &&
      Yup.string().email(languageData?.invalidEmail).max(50).required(languageData?.emailRequired),

    password: regFields.password &&
      Yup.string()
        .min(8, languageData?.min8Chars)
        .max(50, languageData?.max50Characters)
        .matches(/[a-z]/, languageData?.mustHaveLowercase) // at least one lowercase
        .matches(/[A-Z]/, languageData?.mustHaveUppercase) // at least one uppercase
        .matches(/\d/, languageData?.mustHaveNumber) // at least one number
        .matches(/[^a-zA-Z0-9]/, languageData?.mustHaveSpecialChar) // at least one special character
        .matches(/^\S*$/, languageData?.noSpacesAllowed) // no space allowed
        .required(languageData?.passwordRequired)
        .test('password-not-match-email-or-username', languageData?.passwordCannotMatchEmailOrUsername || 'Password cannot match email or username',
          function (value) {
            const { email, username } = this.parent
            if (!value) return true // Skip test if password is empty (other validators will catch it)

            const lowerPassword = value.toLowerCase()
            const lowerEmail = email?.toLowerCase() || ''
            const lowerUsername = username?.toLowerCase() || ''

            return (
              lowerPassword !== lowerEmail &&
              lowerPassword !== lowerUsername
            )
          }),

    confirmPassword: regFields.confirmPassword &&
      Yup.string()
        .max(50)
        .oneOf([Yup.ref('password'), null], languageData?.matchPassword)
        .required(languageData?.confirmPasswordRequired),

    firstName: regFields.firstName &&
      Yup.string()
        .min(3, languageData?.fName3Chars).matches(NAME_REGEX, languageData?.signup_invalidNameChars)
        .max(50, languageData?.max50Characters).required(languageData?.firstNameRequired),

    lastName: regFields.lastName === 2
      ? Yup.string()
        .min(3, languageData?.lName3Chars).max(50, languageData?.max50Characters).matches(NAME_REGEX, languageData?.signup_invalidNameChars).required(languageData?.lastNameRequired)
      : Yup.string()
        .min(3, languageData?.lName3Chars).max(50, languageData?.max50Characters).matches(NAME_REGEX, languageData?.signup_invalidNameChars).nullable(),

    phone: regFields.phone === 2
      ? createPhoneSchema(languageData?.invalidPhone).required(languageData?.phoneRequired).nullable()
      : regFields.phone !== 0 && createPhoneSchema(languageData?.invalidPhone).nullable(),

    dateOfBirth: regFields.dateOfBirth === 2
      ? Yup.string()
        .nullable() // Allow null values for the date
        .test('complete-date', languageData?.dateOfBirthRequired, function (value) {
          if (!value || value === 'YYYY/MM/DD') return false
          const [year, month, day] = value.split('/')
          return year !== 'YYYY' && month !== 'MM' && day !== 'DD'
        })
        .test('valid-date', languageData?.validDOB, function (value) {
          if (!value) return false
          const [year, month, day] = value.split('/')
          const parsed = parse(`${year}-${month}-${day}`, 'yyyy-MM-dd', new Date())
          return isValid(parsed) // Ensure the date is valid
        })
        .test('age', languageData?.mustBe18, function (value) {
          if (!value) return false
          const [year, month, day] = value.split('/')
          const dob = parse(`${year}-${month}-${day}`, 'yyyy-MM-dd', new Date())
          if (!isValid(dob)) return false
          const today = new Date()
          return !isBefore(today, addYears(dob, 18)) // Ensure the user is 18+
        })
      : regFields.dateOfBirth !== 0 && Yup.string().nullable(), // Allow nullable value for empty field

    gender: regFields.gender === 2
      ? Yup.string().required(languageData?.genderRequired)
      : regFields.gender !== 0 && Yup.string().nullable(),

    username: regFields.username === 2
      ? Yup.string().max(20, languageData?.max20Characters)
        .min(3, languageData?.min3Characters)
        .matches(/^[A-Za-z][A-Za-z0-9._]*$/, languageData?.userNameAllowed)
        .required(languageData?.userNameRequired)
      : regFields.username !== 0 && Yup.string().max(20, languageData?.max20Characters)
        .min(3, languageData?.min3Characters)
        .matches(/^[A-Za-z][A-Za-z0-9._]*$/, languageData?.userNameAllowed)
        .nullable(),

    address: regFields.address === 2
      ? Yup.string().max(100, languageData?.max100Characters)
        .required(languageData?.addressRequired)
        .nullable()
      : regFields.address !== 0 && Yup.string().max(100, languageData?.max100Characters)
        .nullable(),

    city: regFields.address === 2
      ? Yup.string().max(50, languageData?.max50Characters)
        .required(languageData?.cityRequired).nullable()
      : regFields.address !== 0 && Yup.string()
        .max(50, languageData?.max50Characters).nullable(),

    zipCode: regFields.address === 2
      ? Yup.string()
        .required(languageData?.postCodeRequired)
        .matches(ZIP_CODE_REGEX, languageData?.zipCodeValidationMessage)
        .nullable()
      : regFields.address !== 0 && Yup.string()
        .nullable(),

    currencyCode: regFields.currencyCode &&
      Yup.string().required(languageData?.currencyCodeRequired),

    countryCode: regFields.countryCode === 2
      ? Yup.string()
        .required(languageData?.countryRequired)
        .test(countryAllowedTest(allowedCountryCodes, languageData))
      : regFields.countryCode !== 0 &&
        Yup.string()
          .nullable()
          .test(countryAllowedTest(allowedCountryCodes, languageData)),

    preferredLanguage: regFields.preferredLanguage === 2
      ? Yup.string().required(languageData?.preferredLanguageRequired)
      : regFields.preferredLanguage !== 0 && Yup.string().nullable(),

    affiliateCode: regFields.affiliateCode === 2
      ? Yup.string().max(50, languageData?.max50Characters).required(languageData?.affiliateCodeRequired)
      : regFields.affiliateCode !== 0 && Yup.string().max(50, languageData?.max50Characters).nullable()
  })
